<template>
  <v-card class="mt-n2" ref="dtContainer">
    <v-toolbar color="primary">
      <v-tabs
        v-model="currentTab"
        align-tabs="start"
        slider-color="#378CDA"
        bg-color="primary"
      >
        <v-tab
          v-for="(floor, n) in floors"
          :key="n"
          :value="n"
          :disabled="floor.isDisabled ? floor.isDisabled : false"
        >
          <span
            class="text-subtitle-1 text-white"
            :class="currentTab === n ? 'font-weight-bold' : ''"
          >
            {{ floor.name }}
          </span>
        </v-tab>
      </v-tabs>
      <v-spacer />
      <v-btn
        class="mr-3 mt-1"
        color="primary"
        variant="flat"
        :append-icon="isFullscreen ? 'mdi-arrow-collapse' : 'mdi-arrow-expand'"
        :ripple="false"
        @click="toggleFullscreen()"
      >
        <span class="mr-1" :class="{ 'mr-2': isFullscreen }">
          {{ isFullscreen ? 'Exit' : 'Fullscreen' }}
        </span>
      </v-btn>
    </v-toolbar>

    <v-tabs-window v-model="currentTab">
      <v-tabs-window-item v-for="(floor, n) in floors" :key="n" :value="n">
        <v-container fluid>
          <ImageLoader v-if="isDataLoading" />
          <ImageViewer class="image-view" v-else>
            <template
              v-slot:Hotspot
              v-if="floor.hotspotsList && !isDataLoading"
            >
              <div v-for="(hotspot, i) in floor.hotspotsList" :key="i">
                <component
                  v-if="hotspot.component && isFloorLoadedMap[floor.loadedName]"
                  :is="hotspot.component"
                  :color="hotspot.color"
                  :label="hotspot.label"
                  :style="{ ...hotspot.style }"
                  :hasTexture="hotspot.hasTexture"
                  @mouseenter="showTooltip(hotspot, floor)"
                  @mouseleave="hideTooltip(floor)"
                  @mousemove="updateTooltipPosition"
                />
              </div>
              <div
                v-for="overlay in floor.overlaysList ?? []"
                :key="overlay.id"
              >
                <component
                  v-if="overlay.component && isFloorLoadedMap[floor.loadedName]"
                  :is="overlay.component"
                  :style="{ ...overlay.style }"
                />
              </div>
            </template>
            <!-- Image Component -->
            <template v-slot:Image>
              <component :is="floor.component" />
            </template>
          </ImageViewer>

          <!-- Tooltip -->
          <div
            v-if="floor.activeTooltip !== null"
            :style="{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              zIndex: 101,
            }"
            class="tooltip"
          >
            <!-- Tooltip content -->
            <DigitalTwinTooltip
              :color="floor.hotspotsList[floor.activeTooltip]?.color"
              :tooltip-info="{
                techInfo: floor.hotspotsList[floor.activeTooltip]?.techInfo,
              }"
            >
              <table>
                <tbody>
                  <tr
                    v-for="(value, key) in floor.hotspotsList[
                      floor.activeTooltip
                    ]?.techInfo?.latestReadings"
                    :key="key"
                  >
                    <td
                      cols="6"
                      class="text-primary text-subtitle-1 text-left pr-7"
                    >
                      {{
                        value && String(key) !== 'location'
                          ? splitCamelCase(capitalizeFirstLetter(`${key}`))
                          : ''
                      }}
                    </td>
                    <td
                      cols="6"
                      class="text-primary text-subtitle-1 text-left pl-7 font-weight-bold"
                    >
                      {{ value }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </DigitalTwinTooltip>
          </div>
        </v-container>
      </v-tabs-window-item>
    </v-tabs-window>
  </v-card>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  reactive,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { useStore } from 'vuex';
import * as allFloors from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/API/floors';
import ImageViewer from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/ImageViewer.vue';

import ImageLoader from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/ImageLoader.vue';

import DigitalTwinTooltip from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DigitalTwinTooltip.vue';

import { capitalizeFirstLetter } from '@/Core.Patterns/Adapter/index.ts';
import { splitCamelCase } from '@/Core.Patterns/Factory/index.ts';

import API from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/API/index';

import { IEndUse } from '@/Core.Service.Domain/Sites/EndUses/types/EndUse';

import {
  IHotspot,
  Color,
} from '@/Core.Service.Domain/Controls/DigitalTwin/types/DigitalTwin.ts';

import { useRoute } from 'vue-router';

const store = useStore();

const floorsLoadedNamesList = ref<string[]>(['FoxStudios']);

const isFloorLoadedMap = reactive<Record<string, boolean>>({});

floorsLoadedNamesList.value.forEach((floorId) => {
  isFloorLoadedMap[floorId] = false;
});

floorsLoadedNamesList.value.forEach((floorId) => {
  watch(
    () => store.getters['DigitalTwinStore/isComponentCached'](floorId),
    (value) => {
      isFloorLoadedMap[floorId] = value;
    },
    { immediate: true }
  );
});

const floors = ref(allFloors.floors);

const latestReadings = ref<IEndUse[]>([]);
const isDataLoading = ref(false);

const route = useRoute();
const intervalId = ref<number | null>(null);
onMounted(async () => {
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  isDataLoading.value = true;
  await getEndUsesLatestReadings();
  isDataLoading.value = false;
  if (intervalId.value === null) {
    intervalId.value = window.setInterval(() => {
      console.log('Fetching latest readings...');
      getEndUsesLatestReadings();
    }, 60 * 5000); // every 5 min
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
  route.meta.isRealTimeReport = false;
});

const getEndUsesLatestReadings = async () => {
  try {
    latestReadings.value = await API.getEndUsesLatestReadings(
      route.params.siteId as string
    );
    setEndUsesForLocation();
    setAmpsForEndUses();
    setKilowattsForEndUses();
    setLocationForEquipment();
    setColorByResourceType();
    setColorByValue();
    route.meta.isRealTimeReport = true;
  } catch (error) {
    route.meta.isRealTimeReport = false;
    return Promise.reject(error);
  }
};

const tooltipPosition = ref({
  top: '0',
  left: '0',
});
const currentTab = ref(0);

const showTooltip = (
  hotspot: IHotspot,
  floor:
    | { activeTooltip: number | null; hotspotsList: Array<IHotspot> }
    | {
        activeTooltip: number | null;
        loadedName: string;
        hotspotsList: Array<IHotspot>;
      }
) => {
  floor.activeTooltip = floor.hotspotsList.findIndex(
    (item) => item.id === hotspot.id
  );
};

const hideTooltip = (floor: { activeTooltip: number | null }) => {
  floor.activeTooltip = null;
};

const updateTooltipPosition = (event: { clientY: number; clientX: number }) => {
  tooltipPosition.value = {
    top: `${event.clientY + 275}px`,
    left: `${event.clientX - 120}px`,
  };
};

// Initialize floors with the latest readings
const setEndUsesForLocation = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList = floor.hotspotsList.filter((hotspot) =>
      latestReadings.value.find(
        (reading) => reading.equipment.id === hotspot.equipmentId
      )
    );
  });
};
const setLocationForEquipment = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList.forEach((hotspot) => {
      const endUse = latestReadings.value.find(
        (reading) => reading.equipment.id === hotspot.equipmentId
      );
      if (endUse && hotspot.techInfo) {
        hotspot.techInfo.location = endUse.equipment.name;
      }
    });
  });
};
const setAmpsForEndUses = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList.forEach((hotspot) => {
      const endUse = latestReadings.value.find(
        (reading) =>
          reading.unitOfMeasure.includes('phase 1 amp(s)') &&
          reading.equipment.id === hotspot.equipmentId
      );
      if (endUse) {
        if (hotspot.techInfo?.latestReadings) {
          hotspot.techInfo.latestReadings.amps =
            endUse.latestReading.toFixed(2);
        }
      } else {
        if (hotspot.techInfo?.latestReadings) {
          hotspot.techInfo.latestReadings.amps = '';
        }
      }
    });
  });
};
const setKilowattsForEndUses = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList.forEach((hotspot) => {
      const endUse = latestReadings.value.find(
        (reading) =>
          reading.unitOfMeasure.includes('kilowatt(s)') &&
          reading.equipment.id === hotspot.equipmentId
      );
      if (endUse) {
        if (hotspot.techInfo?.latestReadings) {
          hotspot.techInfo.latestReadings.kilowatts =
            endUse.latestReading.toFixed(2);
        }
      } else {
        if (hotspot.techInfo?.latestReadings) {
          hotspot.techInfo.latestReadings.kilowatts = '';
        }
      }
    });
  });
};
const setColorByResourceType = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList.forEach((hotspot) => {
      const endUse = latestReadings.value.find(
        (reading) => reading.equipment.id === hotspot.equipmentId
      );
      if (endUse) {
        if (endUse.resourceType.id === 1) {
          hotspot.color = Color.Electricity;
        } else if (endUse.resourceType.id === 3) {
          hotspot.hasTexture = true;
          hotspot.color = Color.Solar;
        }
      }
    });
  });
};
const setColorByValue = () => {
  floors.value.forEach((floor) => {
    floor.hotspotsList.forEach((hotspot) => {
      if (
        (hotspot.techInfo?.latestReadings?.amps !== '' &&
          Number(hotspot.techInfo?.latestReadings?.amps) <= 0) ||
        (hotspot.techInfo?.latestReadings?.kilowatts !== '' &&
          Number(hotspot.techInfo?.latestReadings?.kilowatts) <= 0)
      ) {
        hotspot.color = Color.Warning;
      }
    });
  });
};

const isFullscreen = ref(false);
const dtContainerRef = useTemplateRef('dtContainer');

const toggleFullscreen = async () => {
  const el = dtContainerRef.value;

  if (!el) return;

  try {
    if (!document.fullscreenElement) {
      await el.$el.requestFullscreen();
      isFullscreen.value = true;
    } else {
      await document.exitFullscreen();
      isFullscreen.value = false;
    }
  } catch (err) {
    return Promise.reject(`Error toggling fullscreen: ${err}`);
  }
};

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement;
  const imageView = document.querySelector('.image-view') as HTMLElement | null;
  if (imageView) {
    imageView.style.height = isFullscreen.value ? '100vh' : '75vh';
  }
};
</script>

<style lang="scss" scoped>
.image-view {
  width: auto;
  height: 75vh;
}

.tooltip {
  position: absolute;
  min-width: 250px;
  min-height: 500px;
  color: white;
  border-radius: 5px;
  white-space: nowrap;
  pointer-events: none;
  transform: translate(-50%, -100%);
}

.v-alert__border {
  opacity: 1 !important;
}

ul {
  list-style-type: none;
  padding-top: 15px;
}

.columns-wrapper {
  display: flex;
  gap: 0;
}

.column {
  list-style: none;
  padding: 0;
  margin: 0;
}

:deep(.v-tab__slider) {
  height: 6px !important;
}
</style>
